import { CheckCircleFilled, ExclamationCircleFilled, SyncOutlined } from '@ant-design/icons';

import { PaymentStatus, PortalPaymentFragment } from '@willow/graphql-iso/src/portal';
import { paymentAmountFormat, paymentDateFormat } from '@willow/shared-web';
import { CompanyId, LoanId } from '@willow/types-iso';

import { CancelPayment } from './CancelPayment';

import './PaymentDetails.scss';

interface Props {
  companyId: CompanyId;
  loanId: LoanId;
  payments: PortalPaymentFragment[];
  refetch: (poll?: boolean) => void;
}

export const PaymentDetails = ({ companyId, loanId, payments, refetch }: Props) => (
  <section className="payment-details u-border-1 u-border-color-bark1 u-border-r-3 u-bg-white ">
    <h2>Recent Payment Details</h2>

    {payments.length === 0 ? (
      <div className="mt-3">
        <p className="u-fs-3 u-color-bark3">No recent payments made</p>
      </div>
    ) : (
      <ul className="mt-3">
        {payments.map((payment) => {
          let { id, total, paymentDate, status } = payment || {};
          const totalFormatted = paymentAmountFormat(total);
          const dateFormatted = paymentDateFormat(paymentDate);
          const dateFormattedMobile = paymentDateFormat(paymentDate, true);
          const formattedStatus = formatPaymentStatus(status);

          return (
            <li key={`payment-details-${id}`} className="payment-details__container">
              <div className="payment-details__item">
                <div className="payment-details__item__info">
                  <div className="d-flex gap-5 align-items-center">
                    <div className="payment-details__item__info__total">{totalFormatted}</div>
                  </div>

                  <div className="payment-details__item__info__date tablet">Submitted on {dateFormatted}</div>
                  <div className="payment-details__item__info__date mobile">{dateFormattedMobile}</div>
                </div>

                <div className="payment-details__item__processing d-flex align-items-center gap-2">
                  <div className="payment-details__item__processing__icon">
                    {status === 'PENDING' && <SyncOutlined className="u-fill-current u-color-blue1" />}
                    {status === 'PROCESSED' && <CheckCircleFilled className="u-fill-current u-color-willow" />}
                    {status === 'FAILED' && <ExclamationCircleFilled className="u-fill-current u-color-red1" />}
                    {status === 'CANCELLED' && <ExclamationCircleFilled className="u-fill-current u-color-red1" />}
                  </div>
                  {formattedStatus}
                </div>
              </div>

              {status === 'PENDING' && (
                <div className="w-100 mt-2">
                  <CancelPayment companyId={companyId} loanId={loanId} payment={payment} refetch={refetch} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

const formatPaymentStatus = (status: PaymentStatus): string => {
  switch (status) {
    case 'PENDING':
      return 'Processing';
    case 'PROCESSED':
      return 'Complete';
    case 'FAILED':
      return 'Failed';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return 'Unknown';
  }
};
